import React from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Global Error Boundary — catches any uncaught React render/runtime error,
 * displays a clean fallback UI, and logs the error to the backend so
 * admins can see production errors in real time.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: String(error?.message || error) };
  }

  componentDidCatch(error, errorInfo) {
    // Best-effort async log — never blocks UI
    try {
      const payload = {
        message: String(error?.message || error),
        stack: String(error?.stack || "").slice(0, 4000),
        component_stack: String(errorInfo?.componentStack || "").slice(0, 4000),
        url: typeof window !== "undefined" ? window.location.href : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 400) : "",
      };
      const token = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
      axios.post(`${API}/api/frontend/errors`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        timeout: 6000,
      }).catch(() => {});
    } catch {
      /* ignore */
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, errorMessage: "" });
    try {
      window.location.assign("/dashboard");
    } catch {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        className="min-h-screen bg-[var(--surface)] flex items-center justify-center px-5"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-sm w-full text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500/15 mx-auto grid place-items-center mb-6">
            <span className="text-4xl">🛠️</span>
          </div>
          <h1 className="font-display text-2xl font-black text-[var(--text)] leading-tight">
            Désolé, un problème est survenu
          </h1>
          <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
            Prosperr a rencontré une erreur sur cette page. Nos équipes en ont été notifiées automatiquement.
          </p>
          <button
            onClick={this.handleReload}
            data-testid="error-back-btn"
            className="mt-6 w-full bg-emerald-500 hover:bg-emerald-600 text-[#0B0B0F] rounded-full py-3.5 font-bold text-sm active:scale-[0.99] transition-all"
          >
            Retour au dashboard
          </button>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 w-full border border-[var(--border)] rounded-full py-3 text-sm font-semibold text-zinc-400 hover:text-emerald-300 hover:border-emerald-500/50 transition-all"
          >
            Réessayer
          </button>
          {this.state.errorMessage && (
            <details className="mt-4 text-left">
              <summary className="text-[11px] text-zinc-500 cursor-pointer">Détail technique</summary>
              <pre className="text-[10px] text-rose-300 bg-rose-50 rounded-xl p-3 mt-2 overflow-auto max-h-40 whitespace-pre-wrap">
                {this.state.errorMessage}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
